if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Only the product-page form participates in the Braymont multi-buy.
        // Quick-add cards and other forms retain Shopify's normal one-item workflow.
        const bundle = this.closest('product-info')?.querySelector('[data-bm-bundle]');
        if (bundle) {
          this.onBraymontBundleSubmit(evt, bundle);
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      async onBraymontBundleSubmit(evt, bundle) {
        const chosen=typeof bundle.braymontGetItems==='function'
          ?bundle.braymontGetItems()
          :{error:'Please try again after the offers have finished loading.'};
        if(chosen.error||!Array.isArray(chosen.items)||!chosen.items.length){
          this.handleErrorMessage(chosen.error||'Please choose an available offer.');
          return;
        }
        this.handleErrorMessage();
        this.error=false;
        this.submitButton.setAttribute('aria-disabled','true');
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');
        if(this.cart)this.cart.setActiveElement(document.activeElement);

        const lines=chosen.items.map(item=>({merchandiseId:String(item.id),quantity:item.quantity}));
        const linesUpdateDeferred=this.createCartLinesUpdateEvent(lines);
        const body={items:chosen.items};
        const shopRoot=window.Shopify?.routes?.root||'/';
        const cartRoot=shopRoot.endsWith('/')?shopRoot:shopRoot+'/';
        if(this.cart){
          body.sections=this.cart.getSectionsToRender().map(section=>section.id);
          body.sections_url=window.location.pathname+window.location.search;
        }
        let cartAdditionConfirmed=false;
        try{
          // Shopify's /cart/add can return HTML. Request the real JSON Ajax endpoint.
          const response=await fetch(cartRoot+'cart/add.js',{
            method:'POST',
            headers:{
              'Content-Type':'application/json',
              'Accept':'application/json',
              'X-Requested-With':'XMLHttpRequest'
            },
            credentials:'same-origin',
            body:JSON.stringify(body)
          });
          const raw=await response.text();
          let data;
          try{data=JSON.parse(raw)}catch{
            if(!response.ok)throw new Error('Could not add these pieces. Please try again.');
            // 2xx HTML may mean items were added; never repeat this POST.
            console.warn('[Braymont] Non-JSON cart response; refreshing without adding again.');
            data={};
          }
          if(!response.ok||data.status){
            throw new Error(data.description||data.message||'We could not add those pieces.');
          }
          cartAdditionConfirmed=true;
          this.resolveCartLinesUpdate(linesUpdateDeferred);
          try{
            const sent=publish(PUB_SUB_EVENTS.cartUpdate,{
              source:'product-form',
              productVariantId:String(chosen.items[0].id),
              cartData:data,
              bundleItems:chosen.items
            });
            if(sent && typeof sent.catch==='function')sent.catch(console.error);
          }catch(eventError){console.warn('[Braymont] Cart update event:',eventError);}
          if(this.cart){
            const sectionIds=this.cart.getSectionsToRender().map(section=>section.id);
            let sections=data.sections;
            if(!sections||sectionIds.some(id=>typeof sections[id]!=='string')){
              // A missing section is a display issue, not a failed add.
              const url=cartRoot+'cart?sections='+encodeURIComponent(sectionIds.join(','));
              const sectionsResponse=await fetch(url,{credentials:'same-origin',headers:{'Accept':'application/json'}});
              if(!sectionsResponse.ok)throw new Error('Unable to refresh your bag.');
              sections=await sectionsResponse.json();
            }
            if(sectionIds.some(id=>typeof sections?.[id]!=='string')){
              throw new Error('The bag display could not be refreshed.');
            }
            this.cart.renderContents({...data,sections});
          }else{
            window.location.assign(window.routes?.cart_url||cartRoot+'cart');
          }
        }catch(error){
          console.error('[Braymont] Add to cart:',error);
          if(cartAdditionConfirmed){
            // Do not suggest retrying when the products were added.
            window.location.assign(window.routes?.cart_url||cartRoot+'cart');
            return;
          }
          this.error=true;
          this.handleErrorMessage(error.message||'Could not add these pieces. Please try again.');
          this.dispatchCartErrorEvent(error.message||'Network error','SERVICE_UNAVAILABLE');
          linesUpdateDeferred?.reject(error);
        }finally{
          this.submitButton.classList.remove('loading');
          this.querySelector('.loading__spinner')?.classList.add('hidden');
          if(this.cart&&this.cart.classList.contains('is-empty'))this.cart.classList.remove('is-empty');
          this.submitButton.removeAttribute('aria-disabled');
          CartPerformance.measureFromEvent('add:user-action',evt);
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        const lines=Array.isArray(variantId)?variantId:[{ merchandiseId:variantId, quantity }];
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines,
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
